/**
 * Safe Datadog RUM wrapper that only loads @datadog/browser-rum when initialized.
 *
 * Usage:
 *   import { initDatadog, addError, setUser, clearUser, setUserProperty, setGlobalContextProperty } from 'tio-common/utils/datadog';
 *
 * In application route:
 *   if (ENV.datadog) { await initDatadog(ENV.datadog); }
 *
 * Everywhere else — just call methods directly (they no-op when not initialized):
 *   addError('something broke', { context });
 *   setUser({ id: user.id, name: user.name });
 */
import type { RumInitConfiguration } from '@datadog/browser-rum';

type DatadogRum = typeof import('@datadog/browser-rum').datadogRum;

let rum: DatadogRum | null = null;

export async function initDatadog(config: RumInitConfiguration) {
  const { datadogRum } = await import('@datadog/browser-rum');
  datadogRum.init(config);
  rum = datadogRum;
}

export function addError(...args: Parameters<DatadogRum['addError']>) {
  rum?.addError(...args);
}

export function setUser(...args: Parameters<DatadogRum['setUser']>) {
  rum?.setUser(...args);
}

export function clearUser() {
  rum?.clearUser();
}

export function setUserProperty(...args: Parameters<DatadogRum['setUserProperty']>) {
  rum?.setUserProperty(...args);
}

export function setGlobalContextProperty(
  ...args: Parameters<DatadogRum['setGlobalContextProperty']>
) {
  rum?.setGlobalContextProperty(...args);
}
